// C2 (Figure 14) — the write half of "What each organisation can see".
//
// Kept in its own file, deliberately. OrganisationVisibilityTable's contracted
// purpose is read-only ("Every organisation with a view in, what it can see,
// and what was hidden"), and putting grant/revoke controls inside it would make
// that sentence false. The table still only reads; everything that CHANGES a
// grant lives here, as its own contracted part.
//
// Figure 14 shows exactly three affordances in that grid:
//   · inside an organisation cell with no grant — "— not granted  Grant…"
//   · in the Actions column, on a row that has a grant — "Hide folder…" "Revoke"
//   · in the Actions column, on a row that has none   — "Nothing granted"
// A row that is not the caller's to grant (shared WITH them) gets no actions at
// all; the table renders its own "Not yours to grant — shared with you" and this
// component is never asked for anything.
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createAggregatorGrant,
  revokeAggregatorGrant,
  addAggregatorFolderExclusion,
  getFolders,
} from '../../services/api'
import { organisationName } from './sharingModel'
import type { AggregatorGrant } from '../../types'

// Every action on this screen changes who can see an invention, so none of them
// fire on a single click: the consequence is stated first, in the words the
// inventor would use, and the button that follows is the one that does it.
// Figure 14's own labels carry the ellipsis for exactly this reason.
function Confirm({ question, verb, danger, busy, error, onConfirm, onCancel }: {
  question: string
  verb: string
  danger?: boolean
  busy: boolean
  error: string | null
  onConfirm: () => void
  onCancel: () => void
}) {
  return (
    <div className="mt-1 rounded-md border border-slate-200 bg-white p-2 text-left">
      <p className="mb-2 text-xs text-slate-600">{question}</p>
      {error && <p className="mb-2 text-xs text-rose-600">{error}</p>}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={onConfirm}
          className={`rounded px-2 py-1 text-xs font-semibold text-white disabled:opacity-50 ${
            danger ? 'bg-rose-600 hover:bg-rose-700' : 'bg-iflow-600 hover:bg-iflow-700'
          }`}
        >
          {busy ? 'Working…' : verb}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={onCancel}
          className="rounded border border-slate-300 px-2 py-1 text-xs text-slate-700 disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    </div>
  )
}

function useGrantsRefetch(projectId: string) {
  const qc = useQueryClient()
  // The grid is the record of who can see what, so it is re-read from the
  // server after every change rather than patched locally — a local patch would
  // show the inventor what we BELIEVE happened, which is the one thing this
  // screen must never do. The backend already writes an audit event on each of
  // these routes, so the change feed follows from the same refetch.
  return () => qc.invalidateQueries({ queryKey: ['aggregator-grants', projectId] })
}

function message(e: unknown, fallback: string): string {
  const d = (e as { response?: { data?: { error?: string } } })?.response?.data?.error
  return d || fallback
}

/** Figure 14's in-cell "Grant…" — shown in an organisation column that holds no
 *  grant on this project. */
export function GrantCellAction({ projectId, aggregatorId, aggregatorName }: {
  projectId: string
  aggregatorId: string
  aggregatorName: string
}) {
  const [open, setOpen] = useState(false)
  const refetch = useGrantsRefetch(projectId)
  const m = useMutation({
    mutationFn: () => createAggregatorGrant(projectId, aggregatorId),
    onSuccess: () => { setOpen(false); refetch() },
  })

  // Carries this component's data-part even though it renders inside the
  // TABLE's organisation cell: the screen walker asks that every contracted
  // string be visible inside an element marked with its own part's name, and
  // "Grant…" belongs to this part, not to the read-only table it sits in.
  if (!open) {
    return (
      <span data-part="OrganisationVisibilityActions" className="whitespace-nowrap">
        <button
          type="button"
          onClick={() => { m.reset(); setOpen(true) }}
          className="ml-1.5 whitespace-nowrap text-iflow-700 underline hover:text-iflow-900"
        >
          Grant…
        </button>
      </span>
    )
  }
  return (
    <Confirm
      question={`Give ${aggregatorName} a view into this project. They will be able to read every document in it except folders you hide. You can revoke this at any time.`}
      verb="Grant access"
      busy={m.isPending}
      error={m.isError ? message(m.error, 'Could not grant access.') : null}
      onConfirm={() => m.mutate()}
      onCancel={() => setOpen(false)}
    />
  )
}

/** One organisation's grant on one row, already chosen, in one of the two
 *  actions. The cell above owns the choosing; this owns the doing. */
function GrantActions({ projectId, grant, label, mode, onDone }: {
  projectId: string
  grant: AggregatorGrant
  label: string | null
  mode: 'hide' | 'revoke'
  onDone: () => void
}) {
  const [folderId, setFolderId] = useState('')
  const refetch = useGrantsRefetch(projectId)
  const orgName = organisationName(grant.aggregator_name, grant.aggregator_id)

  // Only fetched once the inventor actually opens the picker: this component is
  // rendered once per row, and eagerly listing folders for ten projects would be
  // ten requests to answer a question nobody asked.
  const folders = useQuery({
    queryKey: ['folders', projectId],
    queryFn: () => getFolders(projectId),
    enabled: mode === 'hide',
    staleTime: 60 * 1000,
  })
  const excluded = new Set(grant.exclusions.map((e) => e.folder_id))
  const selectable = (folders.data ?? []).filter((f) => !excluded.has(f.folder_id))

  const revoke = useMutation({
    mutationFn: () => revokeAggregatorGrant(projectId, grant.aggregator_id),
    onSuccess: () => { onDone(); refetch() },
  })
  const hide = useMutation({
    mutationFn: () => addAggregatorFolderExclusion(projectId, grant.aggregator_id, [folderId]),
    onSuccess: () => { setFolderId(''); onDone(); refetch() },
  })

  if (mode === 'revoke') {
    return (
      <>
        {label && <p className="mb-1 text-right text-[11px] text-slate-400">{label}</p>}
      <Confirm
        question={`Revoke ${orgName}'s view of this project. They lose access to every document in it immediately. Anything they already read is not recalled.`}
        verb="Revoke access"
        danger
        busy={revoke.isPending}
        error={revoke.isError ? message(revoke.error, 'Could not revoke access.') : null}
        onConfirm={() => revoke.mutate()}
        onCancel={onDone}
        />
      </>
    )
  }

  if (mode === 'hide') {
    // The organisation is named above every hide/revoke confirmation on a row
    // that has more than one — "Revoke access" with no name is the one question
    // this screen must never leave open.
    const named = label ? <p className="mb-1 text-right text-[11px] text-slate-400">{label}</p> : null
    if (folders.isPending) {
      return <>{named}<p className="mt-1 text-xs text-slate-400">Loading folders…</p></>
    }
    if (folders.isError) {
      return <>{named}<p className="mt-1 text-xs text-rose-600">Could not load this project&apos;s folders.</p></>
    }
    if (selectable.length === 0) {
      return (
        <div className="mt-1 text-left">
          {named}
          <p className="text-xs text-slate-500">
            {(folders.data ?? []).length === 0
              ? 'This project has no folders to hide.'
              : `Every folder is already hidden from ${orgName}.`}
          </p>
          <button type="button" onClick={onDone} className="mt-1 text-xs text-slate-600 underline">
            Close
          </button>
        </div>
      )
    }
    return (
      <div className="mt-1 text-left">
        {named}
        <label className="mb-1 block text-xs text-slate-600" htmlFor={`hide-${projectId}-${grant.aggregator_id}`}>
          Folder to hide from {orgName}
        </label>
        <select
          id={`hide-${projectId}-${grant.aggregator_id}`}
          value={folderId}
          onChange={(e) => setFolderId(e.target.value)}
          className="mb-1 w-full rounded border border-slate-300 px-2 py-1 text-xs"
        >
          <option value="">Choose a folder…</option>
          {selectable.map((f) => (
            <option key={f.folder_id} value={f.folder_id}>{f.name}</option>
          ))}
        </select>
        {folderId ? (
          <Confirm
            question={`Hide this folder from ${orgName}. They keep their view of the rest of the project, and stop seeing anything in this folder.`}
            verb="Hide folder"
            busy={hide.isPending}
            error={hide.isError ? message(hide.error, 'Could not hide that folder.') : null}
            onConfirm={() => hide.mutate()}
            onCancel={() => { setFolderId(''); onDone() }}
          />
        ) : (
          <button type="button" onClick={onDone} className="text-xs text-slate-600 underline">
            Cancel
          </button>
        )}
      </div>
    )
  }

  return null
}

/**
 * The whole Actions cell for one project row — ONE "Hide folder… / Revoke"
 * pair, as the figure draws it.
 *
 * It used to render one pair per granted organisation. On charlie's real data
 * that is five stacked pairs with a grey organisation name over each, and the
 * six five-way-granted rows came out about 250px tall — the grid the figure
 * draws as a scannable matrix became a wall. Measured on the round-2 walk.
 *
 * The organisation is still the first thing either action needs, so it moved
 * INSIDE the action: press Revoke on a multi-granted row and the row asks which
 * organisation, then states what revoking that one does before it does it. On a
 * row with a single grant there is nothing to ask, so it goes straight to the
 * confirmation — the extra step exists only where there is a real choice.
 */
export default function OrganisationVisibilityActions({ projectId, grants }: {
  projectId: string
  grants: AggregatorGrant[]
}) {
  const [chosen, setChosen] = useState<string | null>(null)
  const [asking, setAsking] = useState<null | 'hide' | 'revoke'>(null)

  if (grants.length === 0) {
    // Figure 14 puts this in the Actions column, not across the organisation
    // cells: the row still shows "— not granted" per organisation, and the
    // Actions column says there is nothing here to change.
    return <span className="text-slate-500">Nothing granted</span>
  }

  const only = grants.length === 1 ? grants[0] : null
  const picked = chosen ? grants.find((g) => g.aggregator_id === chosen) ?? null : only
  const start = (mode: 'hide' | 'revoke') => {
    setChosen(null)
    setAsking(mode)
  }

  // A choice to make first: which of this row's organisations.
  if (asking && !picked) {
    return (
      <div data-part="OrganisationVisibilityActions" className="text-left">
        <p className="mb-1 text-xs text-slate-600">
          {asking === 'hide' ? 'Hide a folder from which organisation?' : 'Revoke which organisation’s view?'}
        </p>
        <div className="flex flex-wrap justify-end gap-1.5">
          {grants.map((g) => (
            <button
              key={g.aggregator_id}
              type="button"
              onClick={() => setChosen(g.aggregator_id)}
              className="rounded border border-slate-300 px-2 py-1 text-xs text-slate-700 hover:bg-slate-50"
            >
              {organisationName(g.aggregator_name, g.aggregator_id)}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setAsking(null)}
            className="px-2 py-1 text-xs text-slate-600 underline"
          >
            Cancel
          </button>
        </div>
      </div>
    )
  }

  if (asking && picked) {
    return (
      <div data-part="OrganisationVisibilityActions" className="text-left">
        <GrantActions
          projectId={projectId}
          grant={picked}
          mode={asking}
          label={grants.length > 1 ? organisationName(picked.aggregator_name, picked.aggregator_id) : null}
          onDone={() => { setAsking(null); setChosen(null) }}
        />
      </div>
    )
  }

  return (
    /* flex-nowrap, and both labels no-wrap. The figure draws this pair on ONE
       line, and it has to stay one line however many organisation columns are
       to its left: on charlie's five-column grid `flex-wrap` let "Hide folder…"
       break inside itself and drop "Revoke" beneath it, which doubled the
       height of every row in the matrix. The cell's min-width (see
       OrganisationVisibilityTable) is what gives it the room. */
    <div data-part="OrganisationVisibilityActions" className="flex flex-nowrap items-center justify-end gap-2">
      {/* 11px in a 9px-padded button — `.vis .act .btn{padding:4px 9px;
          font-size:11px}` in the figure's own stylesheet, which is SMALLER than
          its other buttons precisely so this pair fits one line in 210px. */}
      <button
        type="button"
        onClick={() => start('hide')}
        className="whitespace-nowrap rounded border border-slate-300 px-2.5 py-1 text-[11px] text-slate-700 hover:bg-slate-50"
      >
        Hide folder…
      </button>
      <button
        type="button"
        onClick={() => start('revoke')}
        className="whitespace-nowrap rounded border border-slate-300 px-2.5 py-1 text-[11px] text-rose-600 hover:bg-rose-50"
      >
        Revoke
      </button>
    </div>
  )
}

/**
 * C2's single primary action — "＋ Grant a project…".
 *
 * The grid already lets the caller grant one organisation on one project from
 * the cell where those two meet, and that is the right control once you have
 * found the row. This is the same act from the other direction: pick the
 * project, pick the organisation, read what it means, do it. It exists because
 * a screen with one primary action makes the next click unambiguous, and on
 * this screen the next click is almost always "let somebody see something".
 *
 * It writes through exactly the same route as the in-cell control and re-reads
 * the grid afterwards, so there is one write path and one source of truth.
 */
export function GrantProjectAction({ projects, organisations }: {
  projects: { projectId: string; name: string }[]
  organisations: { id: string; name: string }[]
}) {
  const [open, setOpen] = useState(false)
  const [projectId, setProjectId] = useState('')
  const [aggregatorId, setAggregatorId] = useState('')
  const qc = useQueryClient()

  const m = useMutation({
    mutationFn: () => createAggregatorGrant(projectId, aggregatorId),
    onSuccess: () => {
      setOpen(false)
      setProjectId('')
      setAggregatorId('')
      qc.invalidateQueries({ queryKey: ['aggregator-grants'] })
    },
  })

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => { m.reset(); setOpen(true) }}
        className="rounded-lg bg-iflow-600 px-3.5 py-2 text-[13px] font-bold text-white hover:bg-iflow-700"
      >
        ＋ Grant a project…
      </button>
    )
  }

  const orgName = organisations.find((o) => o.id === aggregatorId)?.name ?? 'this organisation'
  const ready = Boolean(projectId && aggregatorId)

  return (
    <div data-part="OrganisationVisibilityActions" className="w-[320px] rounded-lg border border-slate-200 bg-white p-3 text-left">
      <label className="mb-1 block text-xs font-medium text-slate-600" htmlFor="grant-project">Project</label>
      <select
        id="grant-project"
        value={projectId}
        onChange={(e) => setProjectId(e.target.value)}
        className="mb-2 w-full rounded border border-slate-300 px-2 py-1 text-xs"
      >
        <option value="">Choose a project…</option>
        {projects.map((p) => <option key={p.projectId} value={p.projectId}>{p.name}</option>)}
      </select>

      <label className="mb-1 block text-xs font-medium text-slate-600" htmlFor="grant-org">Organisation</label>
      <select
        id="grant-org"
        value={aggregatorId}
        onChange={(e) => setAggregatorId(e.target.value)}
        className="mb-2 w-full rounded border border-slate-300 px-2 py-1 text-xs"
      >
        <option value="">Choose an organisation…</option>
        {organisations.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
      </select>

      {organisations.length === 0 && (
        <p className="mb-2 text-xs text-slate-500">
          No organisation has a view into any of your projects yet, and none has
          invited you — so there is nobody to grant to.
        </p>
      )}

      {ready ? (
        <Confirm
          question={`Give ${orgName} a view into this project. They will be able to read every document in it except folders you hide. You can revoke this at any time.`}
          verb="Grant access"
          busy={m.isPending}
          error={m.isError ? message(m.error, 'Could not grant access.') : null}
          onConfirm={() => m.mutate()}
          onCancel={() => setOpen(false)}
        />
      ) : (
        <button type="button" onClick={() => setOpen(false)} className="text-xs text-slate-600 underline">
          Cancel
        </button>
      )}
    </div>
  )
}
